// ./src/components/features/EventVendors/VendorPeopleList/VendorPeopleList.tsx
//
// ROADMAP #170 — a vendor's people: a table on desktop, cards below (buttons on a tablet, a swipe on a phone), like every
// list (owner, 2026-10-20). Each row: the person and their role there, and how to reach them — compact links in the table,
// big Call / Email / Copy buttons on a card (the same `ContactChannels` Contacts uses). On an event's vendor page it also
// says who gets the emails. Used on Library › Vendors (with Their role / Remove) and on an event (read only — its people
// are changed as a whole in the People drawer).
// ⓘ The whole set is shown (a vendor's people are few; the page reads them whole), so there is no Load more.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Skeleton, Table, Badge } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EventVendorPerson, VendorPerson } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { recordHasRight } from "@/utils";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";

import { ContactChannels, ContactChannelsMobile } from "../../Contacts/ContactChannels/ContactChannels";
import { personName } from "../vendorUi";

export type VendorPeopleListPerson = VendorPerson | EventVendorPerson;

export interface VendorPeopleListProps {
  data: VendorPeopleListPerson[];
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  /** True when a search is narrowing the list — then an empty one is "no match", not "none yet". */
  hasQueryNarrowing?: boolean;
  /** Their role there. Absent = read only. */
  onEdit?: (person: VendorPerson) => void;
  /** Off the vendor (they stay in Contacts). Absent = read only. */
  onDelete?: (person: VendorPerson) => void;
  isRemoving: (id?: string) => boolean;
  /** An event's people: say who gets the emails. */
  showEmails?: boolean;
  emptyTitle: string;
  emptyBody: string;
  /** First-run empty state's action. */
  onAdd?: () => void;
  addLabel?: string;
}

interface RowProps {
  person: VendorPeopleListPerson;
  onEdit?: (person: VendorPerson) => void;
  onDelete?: (person: VendorPerson) => void;
  isDeleting: boolean;
  showEmails: boolean;
}

const keyOf = (p: VendorPeopleListPerson): string => ("id" in p && p.id ? p.id : (p.personId ?? ""));
const getsEmails = (p: VendorPeopleListPerson): boolean => "receivesEmails" in p && p.receivesEmails !== false && !!p.email;
/** A row the caller may change: a Library row the caller holds the right on. */
const canEdit = (p: VendorPeopleListPerson): boolean => "id" in p && !!p.id && recordHasRight(p as VendorPerson, "edit");
const canRemove = (p: VendorPeopleListPerson): boolean => "id" in p && !!p.id && recordHasRight(p as VendorPerson, "remove");

const NameCell: React.FC<{ person: VendorPeopleListPerson }> = ({ person }) => (
  <Group gap={6} wrap="wrap" style={{ minWidth: 0 }}>
    <Text size="md" fw={600}>
      {personName(person) || person.email}
    </Text>
    {person.roleTitle ? (
      <Text size="md" c="dimmed">
        {person.roleTitle}
      </Text>
    ) : null}
  </Group>
);

const EmailsBadge: React.FC = () => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Badge size="md" variant="light" color="blue">
      {t("vendors:people.getsEmails", { defaultValue: "Gets the emails" })}
    </Badge>
  );
};

const RowActions: React.FC<RowProps> = ({ person, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {onEdit && canEdit(person) ? (
        <Tooltip label={t("vendors:people.role", { defaultValue: "Their role" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="neutral"
            size="lg"
            disabled={isDeleting}
            onClick={() => onEdit(person as VendorPerson)}
            aria-label={t("vendors:people.role", { defaultValue: "Their role" })}
          >
            <AppIcon iconName="Pencil" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onDelete && canRemove(person) ? (
        <Tooltip label={t("vendors:people.remove", { defaultValue: "Remove from the vendor" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            onClick={() => onDelete(person as VendorPerson)}
            aria-label={t("vendors:people.remove", { defaultValue: "Remove from the vendor" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const PersonCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["vendors"]);
  const { person } = p;
  const hasActions = (!!p.onEdit && canEdit(person)) || (!!p.onDelete && canRemove(person));

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (p.onEdit && canEdit(person)) {
      actions.push({
        key: "edit",
        icon: "Pencil",
        label: t("vendors:people.roleShort", { defaultValue: "Role" }),
        color: "blue",
        onClick: () => p.onEdit?.(person as VendorPerson),
        disabled: p.isDeleting,
      });
    }

    if (p.onDelete && canRemove(person)) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("vendors:people.removeShort", { defaultValue: "Remove" }),
        color: "red",
        onClick: () => p.onDelete?.(person as VendorPerson),
        loading: p.isDeleting,
        disabled: p.isDeleting,
      });
    }

    return actions;
  }, [p, person, t]);

  const cardBody = (showActions: boolean) => (
    <Paper p="md" withBorder radius="md" opacity={p.isDeleting ? 0.5 : 1} style={{ transition: "opacity 150ms ease" }}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <NameCell person={person} />
          <ContactChannelsMobile person={person} />
          {p.showEmails && getsEmails(person) ? (
            <Group>
              <EmailsBadge />
            </Group>
          ) : null}
        </Stack>
        {showActions && hasActions ? <RowActions {...p} /> : null}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const PersonCard = React.memo(PersonCardComponent);

const VendorPeopleListComponent: React.FC<VendorPeopleListProps> = ({
  data,
  isLoaded,
  hasQueryNarrowing = false,
  onEdit,
  onDelete,
  isRemoving,
  showEmails = false,
  emptyTitle,
  emptyBody,
  onAdd,
  addLabel,
}) => {
  const { t } = useTranslation(["vendors"]);
  const { isDesktop } = useAppEnvironment();
  // The tablet card's row buttons (Their role + Remove) — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEdit, onDelete);
  const withActions = cardActions > 0;
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (person: VendorPerson) => {
      closeAllCards();
      onEdit?.(person);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (person: VendorPerson) => {
      closeAllCards();
      onDelete?.(person);
    },
    [onDelete, closeAllCards]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("vendors:people.columns.person", { defaultValue: "Person" })}</Table.Th>
        <Table.Th style={{ width: 320 }}>{t("vendors:people.columns.contact", { defaultValue: "Contact" })}</Table.Th>
        {showEmails ? <Table.Th style={{ width: 170 }}>{t("vendors:people.columns.emails", { defaultValue: "Emails" })}</Table.Th> : null}
        {withActions ? <Table.Th style={{ width: 100 }} /> : null}
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width="50%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="80%" />
                    </Table.Td>
                    {showEmails ? (
                      <Table.Td>
                        <Skeleton height={16} width={110} />
                      </Table.Td>
                    ) : null}
                    {withActions ? (
                      <Table.Td>
                        <Group gap={4} wrap="nowrap" justify="flex-end">
                          {Array.from({ length: cardActions }).map((_, a) => (
                            <Skeleton key={a} circle height={34} width={34} />
                          ))}
                        </Group>
                      </Table.Td>
                    ) : null}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: the name and role, one contact row (Call / Email + Copy), on an event "Gets the emails".
          // ⓘ ONE contact row is the typical card — counted 2026-10-20 (read-only): vendor people with one channel 1, with both 0.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={showEmails ? 3 : 2} sizes={showEmails ? ["md", "control", "sm"] : ["md", "control"]} gap={12} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between" align="flex-start">
              <CardSkeletonLines count={showEmails ? 3 : 2} sizes={showEmails ? ["md", "control", "sm"] : ["md", "control"]} gap={12} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("vendors:people.noMatch", { defaultValue: "Nobody matches your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState title={emptyTitle} description={emptyBody} iconName="Users" actionLabel={onAdd ? addLabel : undefined} onAction={onAdd} size="md" />
      </Paper>
    );
  }

  const rowProps = (person: VendorPeopleListPerson): RowProps => ({
    person,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    isDeleting: isRemoving(keyOf(person)),
    showEmails,
  });

  if (isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table verticalSpacing="sm" horizontalSpacing="md">
          {header}
          <Table.Tbody>
            {data.map((person) => {
              const p = rowProps(person);

              return (
                <Table.Tr key={keyOf(person)} style={{ opacity: p.isDeleting ? 0.5 : 1, transition: "opacity 150ms ease" }}>
                  <Table.Td>
                    <NameCell person={person} />
                  </Table.Td>
                  <Table.Td>
                    <ContactChannels person={person} />
                  </Table.Td>
                  {showEmails ? <Table.Td>{getsEmails(person) ? <EmailsBadge /> : null}</Table.Td> : null}
                  {withActions ? (
                    <Table.Td>
                      <RowActions {...p} />
                    </Table.Td>
                  ) : null}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {data.map((person) => (
        <PersonCard
          key={keyOf(person)}
          {...rowProps(person)}
          forceClose={shouldForceClose(keyOf(person))}
          onForceCloseComplete={() => handleForceCloseComplete(keyOf(person))}
          onSwipeStateChange={(isOpen) => isOpen && openCard(keyOf(person))}
        />
      ))}
    </Stack>
  );
};

export const VendorPeopleList = React.memo(VendorPeopleListComponent);
